/**
 * Lib.Common: Internationalization
 */


import $date from './date';
import $num from './num';
import $string from './string';
import $util from './util';


const ietfParse = str => str.match(/^([a-z]{2,3})(?:-([a-z]{3})(?=$|-))?(?:-([a-z]{4})(?=$|-))?(?:-([a-z]{2}|\d{3})(?=$|-))?/i);
const ietfStringify = part => part.slice(1).filter(Boolean).join('-');

const autoPart = (part, index) => {
    const accept = $locale.accept;
    for (let i = 0; i < accept.length; i++) {
        const lp = ietfParse(accept[i]);
        if (lp[1] == part[1] && lp[index]) {
            return lp[index];
        }
    }
    return null;
}

const autoScript = part => part[1] == 'zh' ? (/^(CN|SG)$/.test(part[4]) ? 'Hans' : 'Hant') : autoPart(part, 3);

// Regions (or language, if region not specified) that begin their week on Saturday or Sunday
const regionSat = 'AEAFBHDJDZEGIQIRJOKWLYOMQASDSY'.match(/../g);
const regionSun = 'AGARASAUBDBRBSBTBWBZCACNCODMDOETGTGUHKHNIDILINJMJPKEKHKRLAMHMMMOMTMXMZNINPPAPEPHPKPRPTPYSASGSVTHTTTWUMUSVEVIWSYEZAZW'.match(/../g);
const languageSat = ['ar','arq','arz','fa'];
const languageSun = 'amasbndzengnguhehiidjajvkmknkolomhmlmrmtmyneomorpapssdsmsnsutatethtnurzhzu'.match(/../g);

const all = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'it', name: 'Italiano' },
    { code: 'ru', name: 'Pусский' },
    { code: 'zh-Hans', name: '中文简体' },
    { code: 'zh-Hant', name: '中文繁體' },
    { code: 'ja', name: '日本語' },
    { code: 'ar', name: 'العربية' },
    { code: 'he', name: 'עִברִית' }
];

const data = {
    'en': require('../../i18n/en.json'),
    'de': require('../../i18n/de.json'),
    'es': require('../../i18n/es.json'),
    'fr': require('../../i18n/fr.json'),
    'it': require('../../i18n/it.json'),
    'ru': require('../../i18n/ru.json'),
    'zh-Hans': require('../../i18n/zh-Hans.json'),
    'zh-Hant': require('../../i18n/zh-Hant.json'),
    'ja': require('../../i18n/ja.json'),
    'ar': require('../../i18n/ar.json'),
    'he': require('../../i18n/he.json')
};


const $locale = {
    all,
    accept: null,
    current: null,
    language: null,
    extlang: null,
    script: null,
    region: null,
    name: null,
    data: {},


    /** DEPRECATED */
    en: {},
    add(language, translations) {
        if (language == 'en') {
            $locale.en = translations;
            Vue.prototype.$t = key => $locale.en[key] || key;
        }
    },


    /**
     * Change the current language. This populates various `$locale` fields, which may trigger
     * `ib-application` to load translation data via object or fetch. It also populates
     * lib.common's own translation data.
     */
    use(language) {
        let accept = (navigator.languages && navigator.languages.length) ? navigator.languages.slice() : [navigator.language || 'en'];
        if (language) {
            accept.unshift(ietfStringify(ietfParse(language.replace(/_/g, '-'))));
        }

        // Remove duplicates and modernize deprecated codes
        $locale.accept = accept = accept
            .filter((lc,i) => accept.indexOf(lc) == i)
            .map(lc => lc.replace(/^(iw|yi|id)(?=-|$)/, lang => ({iw:'he',yi:'ji',id:'in'}[lang])));
        $locale.set(accept[0]);
    },


    set(language, callback, persist) {
        if (language == null) {
            language = $locale.current;
        } else {
            const part = ietfParse(language);
            if (!part) {
                throw new RangeError('$locale.set: invalid language: ' + language);
            }

            const current = ietfStringify(part);
            if ($locale.current != current) {
                $locale.current = current;
                $locale.language = part[1];
                $locale.extlang = part[2] || autoPart(part, 2);
                $locale.script = part[3] || autoScript(part);
                $locale.region = part[4] || autoPart(part, 4);
                $locale.name =
                    (all.find(e => e.code == $locale.language + '-' + $locale.script) ||
                     all.find(e => e.code == $locale.language) ||
                     all[0]).name;

                const dopt = {
                    numberingSystem: 'latn',
                    calendar: 'gregory',
                    timeZone: 'UTC',
                    hour: 'numeric'
                };
                const fmt24 = new Intl.DateTimeFormat(current, dopt);
                const fmt12 = new Intl.DateTimeFormat(current, { hour12: true, ...dopt });
                $locale.date = {
                    weekStart:
                        $locale.region ?
                            (regionSun.includes($locale.region) ? 6 :
                             regionSat.includes($locale.region) ? 5 : 0) :
                            (languageSun.includes($locale.language) ? 6 :
                             languageSat.includes($locale.language) ? 5 : 0),
                    hour12: parseInt(fmt24.format(468e5)) == 1,
                    period: [
                        fmt12.format(0).slice(3),
                        fmt12.format(432e5).slice(3)
                    ]
                };

                const fmtd = new Intl.NumberFormat($locale.ncurrent || current, { numberingSystem: 'latn' });
                const fmtp = new Intl.NumberFormat($locale.ncurrent || current, { numberingSystem: 'latn', style: 'percent' });
                const nsample = fmtd.formatToParts(10000.1);
                const ngroup = nsample.find(part => part.type == 'group');
                const ndecimal = nsample.find(part => part.type == 'decimal');
                $locale.number = {
                    group: ngroup && ndecimal ? ngroup.value : ',',
                    decimal: ngroup && ndecimal ? ndecimal.value : '.',
                    percentSign: (fmtp.format(0).match(/\s*%/) || ['%'])[0]
                };

                $locale.data = {
                    ...data['en'],
                    ...data[$locale.language],
                    ...data[$locale.language + '-' + $locale.script]
                };

                $date._use(current);
                $num._use($locale.ncurrent || current);

                if (typeof moment !== 'undefined') {
                    moment.locale(language);
                }
            }
        }

        // legacy junk
        if (callback) { setTimeout(callback); }
        if (persist) { return $locale.setCookie(); }
    },


    /**
     * Set the `IB_LANG` cookie to the value that best approximates the currently set language.
     * Non-Lib.Common applications do not deal well with unknown values so we are careful.
     */
    setCookie() {
        let IB_LANG;
        if (['en','de','es','fr','it','ja','ru','he','ar'].includes($locale.language)) {
            IB_LANG = $locale.language;
        } else if ($locale.language == 'zh') {
            IB_LANG = $locale.script == 'Hans' || /^(CN|SG)$/.test($locale.region) ? 'zh_CN' : 'zh_TW';
        }
        if (IB_LANG) {
            $util.cookie('IB_LANG', IB_LANG);
        } else {
            console.warn('Cannot set IB_LANG=' + $locale.language);
        }
        return IB_LANG;
    }
};


if (typeof Vue !== 'undefined') {
    Vue.util.defineReactive(Vue.prototype, '$locale', $locale);
    Vue.util.defineReactive(Vue.prototype, '$n', $string.fromNumber);
    Vue.util.defineReactive(Vue.prototype, '$t', key => key);
}


export default $locale;
